// src/pages/Departamento/Departamento.js

import { useEffect, useState, useCallback, useMemo } from 'react';
import CollapsibleTable from '../../components/table';
import ModalDepartamento from '../../components/ModalDepartamento';
import DoubleConfirmModal from '../../components/DoubleConfirmModal';
import { useSession } from '../../components/hooks/useSession';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';
import config from '../../config/config';
import axios from 'axios';

import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import BotonAgregar from '../../components/BotonAgregar';

import ExportButton from '../../components/BotonEx';
import { Box } from '@mui/material';

// Helpers sesión
const getSessionString = (key) => sessionStorage.getItem(key) || '';

const getSessionArray = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    if (!value) return [];
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error('Error parseando', key, e);
    return [];
  }
};

// Normaliza idEmpresa que puede venir como:
//  - [2]
//  - "2"
//  - "[1,2,3]"
const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];

  if (Array.isArray(idEmpresa)) {
    return idEmpresa.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  }

  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();

    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) {
          return arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
        }
      } catch {}
    }

    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }

  const n = Number(idEmpresa);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

export default function Departamento() {
  const apiUrl = config.apiBaseUrl;

  // useSession se mantiene por compatibilidad
  useSession();

  // ─── Datos de sesión reactivos ────────────────────────────────────────
  const [loggedInUsername, setLoggedInUsername] = useState(getSessionString('loggedInUsername'));
  const [idEmpresa, setIdEmpresa] = useState(getSessionArray('EmpresaId'));

  // UsuarioId para persistencia
  const usuarioId = useMemo(() => Number(sessionStorage.getItem('UsuarioId') || 0), []);

  // Key estable
  const empresaIdsKey = useMemo(() => {
    const arr = normalizeEmpresaArray(idEmpresa);
    return arr.length ? arr.join(',') : '';
  }, [idEmpresa]);

  const empresaIds = useMemo(() => {
    if (!empresaIdsKey) return [];
    return empresaIdsKey
      .split(',')
      .map((n) => Number(n))
      .filter((x) => Number.isFinite(x) && x > 0);
  }, [empresaIdsKey]);

  // Para preferencias: TODAS => 0
  const empresaIdPref = useMemo(() => {
    return empresaIds.length > 1 ? 0 : (empresaIds[0] ?? 0);
  }, [empresaIds]);

  // Escuchar cambio de empresa
  useEffect(() => {
    const handleEmpresaChange = () => {
      const nuevosIds = getSessionArray('EmpresaId');
      const nuevoUser = getSessionString('loggedInUsername');

      setIdEmpresa(nuevosIds);
      setLoggedInUsername(nuevoUser);
    };

    window.addEventListener('empresaCambiada', handleEmpresaChange);
    return () => window.removeEventListener('empresaCambiada', handleEmpresaChange);
  }, []);

  const [data, setData] = useState([]);
  const [formData, setFormData] = useState({
    NOMBRE_DEPARTAMENTO: '',
    IDEMPRESA: '',
    DEPARTAMENTOID: null
  });
  const [selectedDep, setSelectedDep] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: '',
    action: '',
    row: null,
  });

  const fetchData = useCallback(() => {
    if (!apiUrl) return;
    if (!empresaIdsKey) return;

    axios
      .post(`${apiUrl}/departamentosTabla`, { idEmpresa: empresaIds })
      .then((res) => {
        if (res.data.mensaje) {
          handleOpenSweetAlert(res.data.mensaje, 'error');
          setData({ contenido: [] });
        } else {
          setData(res.data);
        }
      })
      .catch((err) => {
        const mensajeError =
          err.response?.data?.error || 'Error al cargar departamentos';
        handleOpenSweetAlert(mensajeError, 'error');
      });
  }, [apiUrl, empresaIds, empresaIdsKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const abrirModal = useCallback((row = null) => {
    if (row) {
      setSelectedDep(row);
      setFormData({
        NOMBRE_DEPARTAMENTO: row.DEPARTAMENTO || '',
        IDEMPRESA: row.IDEMPRESA === 4 ? '' : row.IDEMPRESA || '',
        DEPARTAMENTOID: row.ID_UNICO || null,
      });
    } else {
      const empresaDefault = empresaIds.length === 1 ? empresaIds[0] : '';

      setSelectedDep(null);
      setFormData({
        NOMBRE_DEPARTAMENTO: '',
        IDEMPRESA: empresaDefault,
        DEPARTAMENTOID: null,
      });
    }
    setModalOpen(true);
  }, [empresaIds]);

  const cerrarModal = () => {
    setModalOpen(false);
    setSelectedDep(null);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const cambiarEstadoDepartamento = useCallback(
    async (row) => {
      const nuevoEstado = row.ACTIVO ? 0 : 1;
      try {
        await axios.post(`${apiUrl}/desactivarDepartamento`, {
          DEPARTAMENTOID: row.ID_UNICO,
          ACTIVO: nuevoEstado,
          SESSION: loggedInUsername,
        });
        handleOpenSweetAlert(
          nuevoEstado ? 'Departamento activado' : 'Departamento desactivado',
          'success'
        );
        fetchData();
      } catch (err) {
        handleOpenSweetAlert('Error al cambiar el estado del departamento', 'error');
      }
    },
    [apiUrl, loggedInUsername, fetchData]
  );

  const openConfirmation = useCallback((action, row, message) => {
    setConfirmModal({ open: true, action, row, message });
  }, []);

  const handleConfirmAction = () => {
    if (confirmModal.action === 'cambiarEstado') {
      cambiarEstadoDepartamento(confirmModal.row);
    }
    setConfirmModal({ open: false, action: '', row: null, message: '' });
  };

  const handleCancelAction = () => {
    setConfirmModal({ open: false, action: '', row: null, message: '' });
  };

  const buttons = useMemo(
    () => [
      {
        id: 1,
        label: 'Editar',
        onClick: abrirModal,
        icon: <EditIcon />,
        buttonColor: '#2196f3',
        buttonFontColor: '#ffffff',
        isVisible: true,
      },
      {
        id: 2,
        label: (row) => (row.ACTIVO ? 'Desactivar' : 'Activar'),
        onClick: (row) => {
          const mensaje = row.ACTIVO
            ? '¿Está seguro de desactivar este departamento?'
            : '¿Está seguro de activar este departamento?';
          openConfirmation('cambiarEstado', row, mensaje);
        },
        icon: (row) => (row.ACTIVO ? <BlockIcon /> : <CheckCircleIcon />),
        buttonColor: (row) => (row.ACTIVO ? '#e53935' : '#43a047'),
        buttonFontColor: '#ffffff',
        isVisible: true,
      },
    ],
    [abrirModal, openConfirmation]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', p: 'clamp(6px, 0.8vw, 12px)', bgcolor: '#f8fafc' }}>
      <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
      <CollapsibleTable
        buttons={buttons}
        dataTable={data}
        density="small"
        compactActions
        maxInlineActions={2}
        showExportButton={true}
        rightButtons={<ExportButton data={data?.contenido || []} />}
        hiddenFields={['IDEMPRESA', 'ID_UNICO']}
        leftButtons={
          <BotonAgregar
            title="Agregar Departamento"
            onClick={() => abrirModal(null)}
          />
        }

        persistEnabled
        persistKey="Departamento"
        persistVista="layout"
        usuarioId={usuarioId}
        empresaId={empresaIdPref}
        apiUrl={apiUrl}
      />
      </Box>

      <ModalDepartamento
        open={modalOpen}
        onClose={cerrarModal}
        onSave={fetchData}
        formData={formData}
        handleInputChange={handleInputChange}
        usuario={loggedInUsername}
        empresaId={empresaIds}
      />

      <DoubleConfirmModal
        open={confirmModal.open}
        message={confirmModal.message}
        handleConfirm={handleConfirmAction}
        handleCancel={handleCancelAction}
      />
    </Box>
  );
}